import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Paper, Button,
    styled, IconButton, MenuItem,
    CircularProgress,
    Breadcrumbs, Link, TextField,
    Alert,
    FormControl,
    InputLabel,
    Select,
    Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useUserRights } from '../../context/UserRightsContext';
import { AuditLoggers } from '../../utils/auditLogger';
import { splitSurrogateKeyTokens } from '../../utils/surrogateKeyTokens';
import { useToast } from '../hooks/useToast';
import Toast from '../common/Toast';
import RefTemplateMappingsEditorDrawer from './GLSync/RefTemplateMappingsEditorDrawer';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
    harmonatePageSx,
    harmonatePageTitleSx,
    harmonateCardSx,
    harmonateProceedButtonSx,
    harmonateFormFieldsSx,
} from '../../theme/harmonateStyles';
import {
  GL_SYNC_TRIAL_BALANCE_DOC_TYPE,
  getDocumentTypeDisplayName,
  isGlSyncUploadDocumentType,
  isYardiAllvuePocDocumentType,
  isYardiAllvueMappingPair,
  sortGlSyncDocumentTypes,
  buildGlSyncFileTypeFolder,
} from '../../constants/glSyncDocumentTypes';

/** Match `GLSyncMapping.jsx` upload zone */
const UploadArea = styled(Box)(({ theme }) => ({
    border: `2px dashed ${ht.line}`,
    borderRadius: '8px',
    padding: '30px 20px',
    textAlign: 'center',
    backgroundColor: ht.listBg,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: ht.blue,
        backgroundColor: ht.secondaryTab,
    },
}));

const HiddenInput = styled('input')({
    display: 'none',
});

/** Same SheetJS loading strategy as `CashRecon/GLTransactionDrawer.jsx` */
const loadXLSX = async () => {
    try {
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
        return XLSX;
    } catch (error) {
        console.warn('Failed to load XLSX from CDN, falling back to npm xlsx:', error);
        const XLSX = await import('xlsx');
        return XLSX;
    }
};

/**
 * Parse CSV / XLS / XLSX like GLTransactionDrawer: chosen sheet (default first), header row, `defval` for empty cells.
 * Uses one code path for all spreadsheet types (avoids naive CSV comma splits).
 */
const listWorkbookSheetNames = async (file) => {
    const name = String(file?.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) return [];
    try {
        const XLSX = await loadXLSX();
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        return Array.isArray(workbook.SheetNames) ? workbook.SheetNames.map((s) => String(s)) : [];
    } catch (error) {
        console.error('Error listing workbook sheets:', error);
        return [];
    }
};

/** Prefer a sheet named TB (trial balance); else first sheet. */
const preferWorkbookSheetName = (sheetNames) => {
    const list = Array.isArray(sheetNames) ? sheetNames.filter(Boolean) : [];
    if (!list.length) return '';
    const tb = list.find((s) => String(s).trim().toUpperCase() === 'TB');
    return tb || list[0];
};

/**
 * Build a new .xlsx/.xls File containing only `sheetName` (so S3 / Snowflake see that sheet as sheet 1).
 */
const buildSingleSheetExcelFile = async (file, sheetName) => {
    const chosen = String(sheetName || '').trim();
    if (!file || !chosen) return file;
    const XLSX = await loadXLSX();
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    if (!workbook.Sheets[chosen]) {
        throw new Error(`Worksheet "${chosen}" was not found in the workbook.`);
    }
    const out = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(out, workbook.Sheets[chosen], chosen.slice(0, 31));
    const ext = String(file.name || '').toLowerCase().endsWith('.xls') && !String(file.name || '').toLowerCase().endsWith('.xlsx')
        ? 'xls'
        : 'xlsx';
    const written = XLSX.write(out, { bookType: ext === 'xls' ? 'xls' : 'xlsx', type: 'array' });
    const mime =
        ext === 'xls'
            ? 'application/vnd.ms-excel'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const base = String(file.name || 'upload').replace(/\.(xlsx|xls)$/i, '');
    return new File([written], `${base}.xlsx`, { type: mime });
};

const extractFieldsFromSheet = async (file, sheetName = null) => {
    try {
        const XLSX = await loadXLSX();
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });

        const names = workbook.SheetNames || [];
        const preferred =
            (sheetName && names.includes(sheetName) && sheetName) ||
            preferWorkbookSheetName(names) ||
            names[0];
        if (!preferred) {
            return { fields: [], data: [], sheetNames: [], sheetName: '' };
        }
        const worksheet = workbook.Sheets[preferred];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        if (!jsonData || jsonData.length === 0) {
            return { fields: [], data: [], sheetNames: names, sheetName: preferred };
        }

        const headers = jsonData[0].map((h) => (h ? h.toString().trim() : '')).filter((h) => h !== '');
        const rows = jsonData.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null));

        const dataRows = rows.map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = row[index] ?? '';
            });
            return obj;
        });

        return { fields: headers, data: dataRows, sheetNames: names, sheetName: preferred };
    } catch (error) {
        console.error('Error extracting fields from sheet:', error);
        return { fields: [], data: [], sheetNames: [], sheetName: '' };
    }
};

/** Case-insensitive column lookup for mapped spreadsheet rows */
const pickRowField = (row, ...aliases) => {
    const keys = Object.keys(row);
    for (const a of aliases) {
        const k = keys.find((rk) => rk.toLowerCase() === a.toLowerCase());
        if (k !== undefined && row[k] !== '' && row[k] != null) return row[k];
    }
    return '';
};

/**
 * @param {string[]} excelHeaderRow — first-sheet header names from the file
 * @param {string[]} requiredTokens — from `splitSurrogateKeyTokens`
 * @param {object|null} template — selected ref template (optional)
 * @param {Record<string, string>} excelColumnByTemplateField — template-key -> selected file header mapping
 * @returns {{ ok: boolean, missing: string[], details: Array<{ token: string, status: 'direct'|'mapped'|'missing', mappedFrom?: string }> }}
 */
const validateSurrogateKeyHeadersInSheet = (
    excelHeaderRow,
    requiredTokens,
    template = null,
    excelColumnByTemplateField = {}
) => {
    if (!requiredTokens.length) return { ok: true, missing: [], details: [] };
    const lower = new Set(
        (excelHeaderRow || []).map((h) => String(h).trim().toLowerCase()).filter((h) => h !== '')
    );
    const templateMappings = template?.mappings || {};
    const missing = [];
    const details = [];
    for (const tok of requiredTokens) {
        const key = String(tok).trim();
        if (!key) continue;
        if (lower.has(key.toLowerCase())) {
            details.push({ token: key, status: 'direct' });
            continue;
        }

        // If a template is selected, allow surrogate-key tokens to be satisfied via template mapping.
        const mappingEntry = Object.entries(templateMappings).find(([templateKeyRaw, m]) => {
            if (!m || m.isActive === false) return false;
            const templateKey = String(templateKeyRaw || '').trim();
            const targetColumn = String(m.targetColumn || '').trim();
            const canonical = REF_TARGET_TO_CANONICAL[targetColumn] || '';
            return (
                templateKey.toLowerCase() === key.toLowerCase() ||
                targetColumn.toLowerCase() === key.toLowerCase() ||
                canonical.toLowerCase() === key.toLowerCase()
            );
        });

        if (mappingEntry) {
            const [templateKeyRaw] = mappingEntry;
            const templateKey = String(templateKeyRaw || '').trim();
            const mappedExcelHeader = String(
                excelColumnByTemplateField?.[templateKey] ??
                excelColumnByTemplateField?.[templateKeyRaw] ??
                ''
            ).trim();
            if (mappedExcelHeader && lower.has(mappedExcelHeader.toLowerCase())) {
                details.push({
                    token: key,
                    status: 'mapped',
                    mappedFrom: mappedExcelHeader,
                });
                continue;
            }
        }

        missing.push(key);
        details.push({ token: key, status: 'missing' });
    }
    return { ok: missing.length === 0, missing, details };
};

/** Short, readable message from Conductor / Spring Boot JSON error bodies */
const formatConductorErrorMessage = (body, status) => {
    const raw =
        (typeof body?.message === 'string' && body.message) ||
        (typeof body?.error === 'string' && body.error) ||
        '';
    if (!raw) return `Request failed (${status})`;
    const errLine = raw.match(/ERROR:\s*([^\n]+)/);
    if (errLine) return errLine[1].trim();
    return raw.length > 600 ? `${raw.slice(0, 600)}…` : raw;
};

/** Excel 1900 date system serial → ISO date (use only for real numeric serials, not "30/12/2022"). */
const excelSerialToIsoDate = (serial) => {
    const excelEpoch = new Date(1900, 0, 1);
    const daysOffset = serial - 1;
    const jsDate = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
    return jsDate.toISOString().split('T')[0];
};

/**
 * Normalize dates for cash-recon bulk: Excel serial numbers, DD/MM/YYYY (EU) vs MM/DD/YYYY, ISO strings.
 * IMPORTANT: parseFloat("30/12/2022") === 30 — must not run Excel-serial logic on slash dates.
 */
const normalizeTransactionDate = (raw) => {
    if (raw === '' || raw == null) return '';
    if (typeof raw === 'number' && !Number.isNaN(raw) && raw > 0 && raw < 100000) {
        return excelSerialToIsoDate(raw);
    }
    const s = String(raw).trim();
    if (s.includes('/')) {
        const parts = s.split('/').map((p) => p.trim());
        if (parts.length === 3) {
            const p0 = parseInt(parts[0], 10);
            const p1 = parseInt(parts[1], 10);
            if (!Number.isNaN(p0) && !Number.isNaN(p1)) {
                let year = parts[2];
                if (/^\d{2}$/.test(year)) year = `20${year}`;
                let day;
                let month;
                if (p0 > 12) {
                    day = p0;
                    month = p1;
                } else if (p1 > 12) {
                    month = p0;
                    day = p1;
                } else {
                    // 01/02/2023 ambiguous: default DD/MM/YYYY (typical for GL exports in this flow)
                    day = p0;
                    month = p1;
                }
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                }
            }
        }
    }
    const num = parseFloat(s);
    if (
        !Number.isNaN(num) &&
        num > 0 &&
        num < 100000 &&
        /^\s*\d+(\.\d+)?\s*$/.test(s)
    ) {
        return excelSerialToIsoDate(num);
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return s;
};

/** Ref-template `targetColumn` → canonical spreadsheet keys consumed by `mapExtractedRowsToGlTransactionsBulk` / `pickRowField`. */
const REF_TARGET_TO_CANONICAL = {
    transactionDate: 'TRANSACTION_DATE',
    glAccountName: 'GL_DESCRIPTION',
    glAccountNo: 'GL_CODE',
    debitAmount: 'DEBIT_AMOUNT',
    creditAmount: 'CREDIT_AMOUNT',
    transactionType: 'TRANSACTION_TYPE',
    fundId: 'FUND_ID',
    entity: 'ENTITY',
};

/** Normalize API `mapping` object keys (trim) and field metadata. */
const normalizeMappingFromApi = (mappingObj) => {
    const out = {};
    if (!mappingObj || typeof mappingObj !== 'object') return out;
    Object.entries(mappingObj).forEach(([rawKey, v]) => {
        const key = String(rawKey).trim();
        if (!key || !v || v.isActive === false) return;
        out[key] = {
            ...v,
            targetColumn: String(v.targetColumn != null ? v.targetColumn : '').trim() || key,
            dataType: v.dataType || 'TEXT',
            isActive: v.isActive !== false,
        };
    });
    return out;
};

const convertTemplateCellValue = (value, dataType) => {
    if (value === '' || value == null) return '';
    const type = (dataType || 'TEXT').toUpperCase();
    if (type === 'DATE') return normalizeTransactionDate(value);
    if (['NUMBER', 'INTEGER', 'FLOAT', 'DECIMAL'].includes(type)) {
        const strValue = String(value).trim();
        const numValue = strValue.replace(/[^0-9.-]/g, '');
        if (numValue === '' || numValue === '-') return '';
        return Number(numValue);
    }
    return String(value).replace(/\s+/g, ' ').trim();
};

/** Remove spreadsheet source column(s) from `out` after copying into canonical keys (avoids duplicate BALANCE + DEBIT_AMOUNT in uploaded JSON). */
const stripMappedSourceColumnsFromRow = (out, sourceRow, excelField, preserveKeysLower = new Set()) => {
    const base = String(excelField || '').trim().toLowerCase();
    if (!base) return;
    for (const k of [...Object.keys(out)]) {
        if (preserveKeysLower.has(k.toLowerCase())) continue;
        if (!Object.prototype.hasOwnProperty.call(sourceRow, k)) continue;
        const kl = k.toLowerCase();
        if (kl === base || kl.startsWith(`${base}_`)) {
            delete out[k];
        }
    }
};

/**
 * Same idea as `GLTransactionDrawer`: map file columns (via template + user picks) onto canonical GL column names
 * so `pickRowField` can resolve values even when the file uses different headers.
 */
const applyRefTemplateToRows = (excelData, template, excelColumnByTemplateField) => {
    const mappings = template?.mappings || {};
    const fieldSpecs = [];
    Object.entries(mappings).forEach(([templateKeyRaw, m]) => {
        if (!m || m.isActive === false) return;
        const templateKey = String(templateKeyRaw).trim();
        const excelField =
            excelColumnByTemplateField?.[templateKey] ?? excelColumnByTemplateField?.[templateKeyRaw];
        if (!excelField || String(excelField).trim() === '') return;
        const targetCol = String(m.targetColumn || '').trim();
        const targetLower = targetCol.toLowerCase();
        const tkUpper = templateKey.toUpperCase();

        if (
            tkUpper === 'AMOUNT' ||
            targetLower === 'signedamount' ||
            targetLower === 'amount' ||
            targetLower === 'net_balance'
        ) {
            fieldSpecs.push({
                excelField: String(excelField).trim(),
                special: 'signedAmount',
                dataType: m.dataType || 'NUMBER',
            });
            return;
        }

        let canonical =
            REF_TARGET_TO_CANONICAL[targetCol] ||
            REF_TARGET_TO_CANONICAL[templateKey] ||
            null;
        if (!canonical && /^[A-Z0-9_]+$/.test(tkUpper)) {
            canonical = tkUpper;
        }
        if (!canonical && targetCol && targetCol === targetCol.toUpperCase() && /^[A-Z0-9_]+$/.test(targetCol)) {
            canonical = targetCol;
        }
        if (!canonical) return;

        fieldSpecs.push({
            excelField: String(excelField).trim(),
            canonical,
            dataType: m.dataType || 'TEXT',
        });
    });
    if (fieldSpecs.length === 0) return excelData;
    return excelData.map((row) => {
        const out = { ...row };
        fieldSpecs.forEach((spec) => {
            if (spec.special === 'signedAmount') {
                const { excelField, dataType } = spec;
                if (!(excelField in row)) return;
                const v = convertTemplateCellValue(row[excelField], dataType);
                const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ''));
                if (Number.isNaN(num) || num === 0) return;
                if (num > 0) out.DEBIT_AMOUNT = num;
                else out.CREDIT_AMOUNT = Math.abs(num);
                stripMappedSourceColumnsFromRow(out, row, excelField, new Set(['debit_amount', 'credit_amount']));
                return;
            }
            const { excelField, canonical, dataType } = spec;
            if (!(excelField in row)) return;
            const v = convertTemplateCellValue(row[excelField], dataType);
            if (v !== '' && v != null) out[canonical] = v;
            stripMappedSourceColumnsFromRow(out, row, excelField, new Set([String(canonical).toLowerCase()]));
        });
        return out;
    });
};

const initializeExcelColumnMap = (template, excelFields) => {
    const next = {};
    Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, m]) => {
        const templateKey = String(templateKeyRaw).trim();
        if (!m || m.isActive === false) return;
        const expectedHeader = String(m.targetColumn || templateKey).trim();
        const auto = excelFields.find((f) => String(f).trim().toLowerCase() === expectedHeader.toLowerCase());
        next[templateKey] = auto ? String(auto) : '';
    });
    return next;
};

/**
 * Map first-sheet rows to `conductor/cash-recon/gl-transactions/bulk` body (same shape as GLTransactionDrawer Snowflake path).
 * Each row includes `systemType: 'GL_SYNC'` like chart-of-account bulk in GLSyncMapping.jsx.
 * `glHeaderId` from POST `conductor/gl-headers` and the same `propertyId` as the header are set on each row before bulk POST.
 *
 * @returns {{ transactions: Array, sameDebitCreditRowCount: number, unequalBothSidesNetRowCount: number }}
 * @param {string|null} surrogateKeyFromSystem — tenant-system `surrogateKey` for selected source GL (not from file).
 */
const mapExtractedRowsToGlTransactionsBulk = (
    dataRows,
    tenantId,
    propertyId,
    sourceSystemName,
    surrogateKeyFromSystem
) => {
    const src = (sourceSystemName || 'GL_SYNC').toUpperCase();
    const surrogateTokens = splitSurrogateKeyTokens(surrogateKeyFromSystem);
    let sameDebitCreditRowCount = 0;
    let unequalBothSidesNetRowCount = 0;
    const transactions = dataRows
        .map((row) => {
            let debitVal = parseFloat(pickRowField(row, 'DEBIT_AMOUNT', 'DEBIT', 'debit_amount')) || 0;
            let creditVal = parseFloat(pickRowField(row, 'CREDIT_AMOUNT', 'CREDIT', 'credit_amount')) || 0;
            const amountSingle =
                parseFloat(
                    pickRowField(
                        row,
                        'AMOUNT',
                        'amount',
                        'VALUE',
                        'TRANSACTION_AMOUNT',
                        'transaction_amount',
                        'NET_AMOUNT'
                    )
                ) || 0;
            if (debitVal <= 0 && creditVal <= 0 && amountSingle !== 0) {
                if (amountSingle > 0) debitVal = amountSingle;
                else creditVal = Math.abs(amountSingle);
            }
            if (debitVal <= 0 && creditVal <= 0) return null;

            // DB check chk_gl_debit_credit_one_side: only one of debit_amount / credit_amount may be set.
            let debitAmount = null;
            let creditAmount = null;
            if (debitVal > 0 && creditVal > 0) {
                const net = debitVal - creditVal;
                if (Math.abs(net) < 1e-9) {
                    sameDebitCreditRowCount += 1;
                    debitAmount = debitVal;
                } else {
                    unequalBothSidesNetRowCount += 1;
                    if (net > 0) {
                        debitAmount = net;
                    } else {
                        creditAmount = -net;
                    }
                }
            } else if (debitVal > 0) {
                debitAmount = debitVal;
            } else {
                creditAmount = creditVal;
            }

            const dateRaw = pickRowField(
                row,
                'TRANSACTION_DATE',
                'transaction_date',
                'DATE',
                'BANK_TRANSACTION_DATE'
            );
            const transactionDate = normalizeTransactionDate(dateRaw);
            if (!transactionDate) return null;

            const glAccountName = String(
                pickRowField(
                    row,
                    'GL_DESCRIPTION',
                    'gl_description',
                    'LEDGER_ACCOUNT_NAME',
                    'ledger_account_name'
                ) || ''
            ).trim();
            if (!glAccountName) return null;

            const amount = debitAmount ?? creditAmount;
            const txnDirection = debitAmount != null ? 'DEBIT' : 'CREDIT';

            const optionalStr = (...aliases) => {
                const v = pickRowField(row, ...aliases);
                if (v == null || v === '') return null;
                const s = String(v).trim();
                return s === '' ? null : s;
            };

            const fundId = optionalStr('FUND_ID', 'fund_id', 'Fund Id', 'FUNDID');
            const entity = optionalStr('ENTITY', 'entity', 'ENTITY_ID', 'entity_id');
            const transactionType = optionalStr('TRANSACTION_TYPE', 'transaction_type', 'Transaction Type');
            const glJoinParts = surrogateTokens
                .map((token) => optionalStr(token))
                .filter((part) => part != null && part !== '');
            const glJoin = glJoinParts.length > 0 ? glJoinParts.join('|') : null;

            return {
                id: null,
                glAccountName,
                transactionDate,
                referenceNo: null,
                description: null,
                debitAmount,
                creditAmount,
                createdAt: null,
                updatedAt: null,
                tenantId,
                propertyId,
                intrgrationId: null,
                companyId: null,
                sourceSystem: src,
                systemType: 'GL_SYNC',
                externalTxnId: null,
                glAccountNo:
                    String(pickRowField(row, 'GL_CODE', 'gl_code') || '').trim() || null,
                postingDate: transactionDate,
                documentDate: transactionDate,
                periodKey: transactionDate.replace(/-/g, '').substring(0, 6),
                documentNo: null,
                batchNo: pickRowField(row, 'BATCH_ID', 'batch_id') || null,
                journalCode:
                    String(pickRowField(row, 'JOURNAL_CODE', 'journal_code', 'JOURNAL') || '').trim() ||
                    null,
                transactionType,
                fundId,
                entity,
                surrogateKey: surrogateKeyFromSystem ?? null,
                glJoin,
                currency: String(pickRowField(row, 'CURRENCY', 'currency') || 'USD'),
                amount,
                txnDirection,
                counterpartyName: null,
                counterpartyId: null,
                reconStatus: 'UNMATCHED',
                reconId: null,
                matchedBankTxnId: null,
                matchConfidence: null,
                matchedBy: null,
                matchedAt: null,
                normalizedRef: null,
                normalizedDesc: null,
                hashKey: null,
                isAdjustment: false,
                adjustmentReason: null,
                approvalStatus: 'POSTED',
                approvedBy: null,
                approvedAt: null,
            };
        })
        .filter(Boolean);
    return { transactions, sameDebitCreditRowCount, unequalBothSidesNetRowCount };
};

/**
 * Prefer default property (name "default" or code "DF"), else first ACTIVE row.
 */
const fetchDefaultCashReconPropertyId = async (tenantId, token) => {
    if (!tenantId || !token) return null;
    try {
        const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
        const url = new URL(`${baseUrl}/conductor/cash-recon/properties/by-tenant-and-status`);
        url.searchParams.set('tenant_id', tenantId);
        url.searchParams.set('status', 'ACTIVE');
        const res = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        });
        if (!res.ok) {
            console.warn('cash-recon properties by-tenant-and-status failed:', res.status);
            return null;
        }
        const list = await res.json().catch(() => []);
        if (!Array.isArray(list) || list.length === 0) return null;
        const byName = list.find((p) => String(p.propertyName || '').toLowerCase().trim() === 'default');
        const byCode = list.find((p) => String(p.propertyCode || '').toUpperCase().trim() === 'DF');
        const chosen = byName || byCode || list[0];
        return chosen?.propertyId ?? null;
    } catch (e) {
        console.error('fetchDefaultCashReconPropertyId:', e);
        return null;
    }
};

// Get API URL from environment or use default
const API_BASE = process.env.REACT_APP_API_URL || '';

const GLSyncGLMappingWorkflow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [extractedData, setExtractedData] = useState([]);
    /** First-row header names from the last parsed file (for template column mapping). */
    const [sheetFields, setSheetFields] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    /** templateFieldKey → file column header (auto-matched to each field’s targetColumn header, case-insensitive) */
    const [excelColumnByTemplateField, setExcelColumnByTemplateField] = useState({});
    const [templateMappingsEditorOpen, setTemplateMappingsEditorOpen] = useState(false);
    /** Raw API `mapping` overlays from the template drawer for this session only (no PUT). Keyed by templateId. */
    const [localRefTemplateMappingById, setLocalRefTemplateMappingById] = useState({});
    /** Surrogate key for this screen only; resets from tenant GL when Source GL / tenant data changes. Not saved to tenant. */
    const [surrogateKeyLocal, setSurrogateKeyLocal] = useState('');
    /** Surrogate key TextField is read-only until the user clicks the edit icon. */
    const [surrogateKeyEditUnlocked, setSurrogateKeyEditUnlocked] = useState(false);
    const [selectedSourceGL, setSelectedSourceGL] = useState('');

    const { hasPermission, isHarmonateUser } = useUserRights();
    const { toastState, showSuccess, showError, hideToast } = useToast();

    // Extract source/destination GL + fund from navigation state (card click)
    const { sourceGL, destinationGL, mappingId, fundId, fundCode, fundName } = location.state || {};
    const cardFundId = fundId ? String(fundId) : null;
    const isQuickMapping = Boolean(sourceGL && destinationGL);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [systems, setSystems] = useState([]);
    const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
    const [loadingSystems, setLoadingSystems] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [errors, setErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [unitOfWorkId, setUnitOfWorkId] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState(''); // Tracks current upload step
    const [uploadDescription, setUploadDescription] = useState('');
    const [mappingOptions, setMappingOptions] = useState({
        'Entity Details': false,
        'Transactions/Commitments': true,
        'Trial Balance': true,
        'Bank Accounts': false,
        'Allocation': true,
    });

    const [isDragging, setIsDragging] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0);
    /** Excel worksheet names from the selected workbook (empty for CSV). */
    const [workbookSheetNames, setWorkbookSheetNames] = useState([]);
    /** Worksheet to parse / land on S3 (TB preferred when present). */
    const [selectedSheetName, setSelectedSheetName] = useState('');
    const uploadInProgressRef = useRef(false);
    const surrogateKeyInputRef = useRef(null);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (isUploading || uploadInProgressRef.current) {
            return;
        }

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const validTypes = ['.csv', '.xls', '.xlsx'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

            if (validTypes.includes(fileExtension)) {
                // Trigger the same file upload handler
                handleFileUpload({ target: { files: [file] } });
            } else {
                setErrors(['Invalid file type. Please upload a CSV or Excel file.']);
            }
        }
    };

    // Get auth token from localStorage
    const getAuthToken = () => {
        return localStorage.getItem('authToken') || localStorage.getItem('token');
    };

    // Fetch document types from API
    const fetchDocumentTypes = async () => {
        setLoadingDocumentTypes(true);
        try {
            const token = getAuthToken();
            const tenantId = localStorage.getItem('tenant_id');

            if (!tenantId) {
                setErrors(['Tenant ID not found in local storage']);
                setLoadingDocumentTypes(false);
                return;
            }

            const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=GL`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                setErrors([`Failed to load document types: ${response.status} - ${errorText}`]);
                setLoadingDocumentTypes(false);
                return;
            }

            const data = await response.json();

            if (!data || data.length === 0) {
                setErrors(['Please configure GL document types for this tenant']);
                setDocumentTypes([]);
                setLoadingDocumentTypes(false);
                return;
            }

            const yardiAllvueCard = isYardiAllvueMappingPair(sourceGL, destinationGL);
            const formattedData = sortGlSyncDocumentTypes(
                data
                    .filter((item) => {
                        const name = getDocumentTypeDisplayName(item);
                        if (!isGlSyncUploadDocumentType(name)) return false;
                        if (yardiAllvueCard) return isYardiAllvuePocDocumentType(name);
                        return !isYardiAllvuePocDocumentType(name);
                    })
                    .map((item) => ({
                        id: item.documentTypeId,
                        name: item.documentTypeName,
                        requireFund: false,
                        requireInvestor: false,
                        isMultiInvestor: false,
                        integrationId: null,
                        parserType: 'DOC_PARSER',
                    }))
            );

            setDocumentTypes(formattedData);
            if (formattedData.length > 0) {
                const trialBalance = formattedData.find(
                    (dt) => dt.name === GL_SYNC_TRIAL_BALANCE_DOC_TYPE
                );
                setSelectedDocumentType((trialBalance || formattedData[0]).id);
                setErrors([]);
            } else {
                setSelectedDocumentType('');
                setErrors([
                    'GL Sync document types are not configured for this tenant. Enable only the onboarded file types for this tenant under document type settings.',
                ]);
            }
        } catch (error) {
            console.error('Error fetching document types:', error);
            setErrors(['Failed to fetch document types. Please try again.']);
        } finally {
            setLoadingDocumentTypes(false);
        }
    };

    // Tenant-scoped systems for Source General Ledger
    const fetchSystems = async () => {
        setLoadingSystems(true);
        try {
            const token = getAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            if (!tenantId) {
                setErrors((prev) => [...prev, 'Tenant ID not found. Please sign in again.']);
                return;
            }

            const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/by-tenant`);
            url.searchParams.set('tenant_id', tenantId);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch tenant systems: ${response.status}`);
            }

            const data = await response.json();
            const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            setSystems(list);

            // Debug logging
            console.log('Navigation state:', {
                sourceGL,
                destinationGL,
                mappingId,
                fundId: cardFundId,
                fundCode,
                fundName,
                isQuickMapping,
            });
            console.log('Systems data:', list);

            // Auto-select source GL if coming from quick mapping
            if (isQuickMapping && sourceGL) {
                // Try to find by name first, then by id
                let sourceSystem = list.find((system) => system.name === sourceGL);
                if (!sourceSystem) {
                    sourceSystem = list.find((system) => system.id === sourceGL);
                }
                console.log('Looking for source system:', sourceGL);
                console.log('Found source system:', sourceSystem);
                if (sourceSystem) {
                    // Use the system's id for the dropdown value
                    setSelectedSourceGL(sourceSystem.id);
                    console.log('Auto-selected source GL ID:', sourceSystem.id);
                } else {
                    console.log('Source system not found in systems data');
                    console.log('Available systems:', list.map((s) => ({ name: s.name, id: s.id })));
                }
            } else if (list.length > 0) {
                setSelectedSourceGL(list[0].id);
                console.log('Default selected source GL ID:', list[0].id);
            }
        } catch (error) {
            console.error('Error fetching systems:', error);
            setErrors(prev => [...prev, `Failed to load systems: ${error.message}`]);
        } finally {
            setLoadingSystems(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchDocumentTypes();
        fetchSystems();
    }, []);

    const fetchTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        try {
            const token = getAuthToken();
            if (!token) {
                setTemplates([]);
                setSelectedTemplate('');
                return;
            }
            if (!selectedSourceGL || !selectedDocumentType) {
                setTemplates([]);
                setSelectedTemplate('');
                return;
            }

            const selectedSourceSystem = systems.find(
                (system) =>
                    system.id === selectedSourceGL ||
                    system.systemId === selectedSourceGL ||
                    system.system_id === selectedSourceGL
            );
            const templateGlSystemId =
                selectedSourceSystem?.systemId || selectedSourceSystem?.system_id || selectedSourceGL;
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
            const url = `${baseUrl}/conductor/ref-templates/by-document-and-gl?documentTypeId=${encodeURIComponent(
                selectedDocumentType
            )}&glSystemId=${encodeURIComponent(templateGlSystemId)}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!resp.ok) {
                setTemplates([]);
                setSelectedTemplate('');
                return;
            }
            const data = await resp.json();

            if (!Array.isArray(data) || data.length === 0) {
                setTemplates([]);
                setSelectedTemplate('');
                return;
            }

            const transformed = data.map((item) => {
                let rawMapping = item.mapping;
                if (typeof rawMapping === 'string') {
                    try {
                        rawMapping = JSON.parse(rawMapping);
                    } catch {
                        rawMapping = {};
                    }
                }
                if (!rawMapping || typeof rawMapping !== 'object') rawMapping = {};
                const tid = item.templateId || item.id;
                return {
                    id: tid,
                    name: item.templateName || item.name || 'Template',
                    description: item.description || item.templateName || '',
                    glSystem: item.glSystemId || selectedSourceGL,
                    mappings: normalizeMappingFromApi(rawMapping),
                    /** Full API row for PUT from the inline template editor (same contract as Reference Templates). */
                    apiTemplate: {
                        ...item,
                        templateId: tid,
                        templateName: item.templateName || item.name || 'Template',
                        mapping: rawMapping,
                    },
                };
            });
            setTemplates(transformed);
            setSelectedTemplate((prev) =>
                prev && transformed.some((x) => x.id === prev) ? prev : transformed[0]?.id || ''
            );
        } catch (err) {
            console.error('Error fetching system-type templates:', err);
            setTemplates([]);
            setSelectedTemplate('');
        } finally {
            setLoadingTemplates(false);
        }
    }, [selectedSourceGL, selectedDocumentType, systems]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    useEffect(() => {
        setLocalRefTemplateMappingById({});
    }, [selectedSourceGL, selectedDocumentType]);

    const activeTemplateForMapping = useMemo(() => {
        const t = templates.find((tm) => tm.id === selectedTemplate);
        if (!t) return undefined;
        const localRaw = localRefTemplateMappingById[selectedTemplate];
        if (!localRaw) return t;
        return {
            ...t,
            mappings: normalizeMappingFromApi(localRaw),
            apiTemplate: { ...t.apiTemplate, mapping: localRaw },
        };
    }, [templates, selectedTemplate, localRefTemplateMappingById]);

    const activeApiTemplateForEditor = useMemo(() => {
        if (!selectedTemplate) return null;
        const base = templates.find((tm) => tm.id === selectedTemplate)?.apiTemplate ?? null;
        if (!base) return null;
        const localRaw = localRefTemplateMappingById[selectedTemplate];
        if (!localRaw) return base;
        return { ...base, mapping: localRaw };
    }, [templates, selectedTemplate, localRefTemplateMappingById]);

    useEffect(() => {
        if (!selectedTemplate) {
            setExcelColumnByTemplateField({});
            return;
        }
        const t = activeTemplateForMapping;
        if (!t) return;
        if (sheetFields.length === 0) {
            setExcelColumnByTemplateField({});
            return;
        }
        setExcelColumnByTemplateField(initializeExcelColumnMap(t, sheetFields));
    }, [selectedTemplate, activeTemplateForMapping, sheetFields]);

    const handleTemplateChange = (templateId) => {
        setSelectedTemplate(templateId);
    };

    // Set selectedSourceGL from navigation state when coming from quick mapping tile
    useEffect(() => {
        if (sourceGL && isQuickMapping) {
            // Find the system ID that matches the sourceGL name
            const matchingSystem = systems.find(system =>
                system.name?.toUpperCase() === sourceGL.toUpperCase()
            );
            if (matchingSystem) {
                setSelectedSourceGL(matchingSystem.id);
            }
        }
    }, [sourceGL, systems, isQuickMapping]);

    // Handle file selection: list Excel sheets (prefer TB), then parse the chosen sheet
    const handleFileUpload = (event) => {
        if (isUploading || uploadInProgressRef.current) {
            return;
        }
        const file = event.target.files[0];
        if (!file) return;

        setUploadedFile(file);
        setUploadProgress(0);
        setErrors([]);
        setWorkbookSheetNames([]);
        setSelectedSheetName('');
        hideToast();

        (async () => {
            try {
                const sheetNames = await listWorkbookSheetNames(file);
                const preferred = preferWorkbookSheetName(sheetNames);
                setWorkbookSheetNames(sheetNames);
                setSelectedSheetName(preferred);

                const { fields, data } = await extractFieldsFromSheet(file, preferred || null);
                const fileNameLower = String(file.name || '').toLowerCase();
                const isSpreadsheet =
                    fileNameLower.endsWith('.xlsx') ||
                    fileNameLower.endsWith('.xls') ||
                    fileNameLower.endsWith('.csv');
                // Allow keep file even when row-1 is not a flat header table (Tally TB layout).
                if (!data.length && !isSpreadsheet) {
                    setErrors(['No data rows found in the file. Check the selected sheet has content.']);
                    setUploadedFile(null);
                    setExtractedData([]);
                    setSheetFields([]);
                    setExcelColumnByTemplateField({});
                    setWorkbookSheetNames([]);
                    setSelectedSheetName('');
                    return;
                }

                setSheetFields(fields);
                setExtractedData(data.map((row, index) => ({ id: index + 1, ...row })));

                let progress = 0;
                const interval = setInterval(() => {
                    progress += 10;
                    setUploadProgress(progress);
                    if (progress >= 100) clearInterval(interval);
                }, 200);
            } catch (err) {
                console.error('Error parsing spreadsheet:', err);
                setErrors(['Error parsing file. Please ensure it is a valid CSV or Excel file.']);
                setUploadedFile(null);
                setUploadProgress(0);
                setExtractedData([]);
                setSheetFields([]);
                setExcelColumnByTemplateField({});
                setWorkbookSheetNames([]);
                setSelectedSheetName('');
            }
        })();
    };

    const handleWorksheetChange = async (sheetName) => {
        const next = String(sheetName || '').trim();
        setSelectedSheetName(next);
        if (!uploadedFile || !next) return;
        try {
            const { fields, data } = await extractFieldsFromSheet(uploadedFile, next);
            setSheetFields(fields);
            setExtractedData(data.map((row, index) => ({ id: index + 1, ...row })));
            setErrors([]);
        } catch (err) {
            console.error('Error re-parsing selected sheet:', err);
            setErrors([`Error reading worksheet "${next}".`]);
        }
    };

    const handleToggleMapping = (option) => {
        setMappingOptions(prev => ({
            ...prev,
            [option]: !prev[option]
        }));
    };

    // Create a new unit of work - fixed implementation
    const createUnitOfWork = async (file) => {
        //setCurrentStep('Creating upload session...');
        try {
            const token = getAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            const userId = localStorage.getItem('user_id');
            const selectedDoc = documentTypes.find((d) => d.id === selectedDocumentType);
            const sourceSystem = systems.find((s) => s.id === selectedSourceGL);
            const fileTypeFolder = buildGlSyncFileTypeFolder(selectedDoc?.name, sourceSystem?.name);

            const cardFundCode = fundCode ? String(fundCode) : null;
            const cardFundName = fundName ? String(fundName) : null;
            const fundAdditionalDetails =
                cardFundId || cardFundCode || cardFundName
                    ? {
                          fund_id: cardFundId,
                          fund_code: cardFundCode,
                          fund_name: cardFundName,
                      }
                    : null;

            const payload = {
                name: 'Test UnitOfWork',
                documents: [{
                    name: file.name,
                    content_type: file.type || 'application/octet-stream',
                    document_type_id: selectedDocumentType,
                    source_general_ledger: fileTypeFolder,
                    destination_general_ledger: destinationGL ? `${destinationGL.toUpperCase()}_TRANSACTIONS` : '',
                    system_id: selectedSourceGL,
                    fund_id: cardFundId,
                    investor_id: null,
                    holding_id: null,
                    // documents table only has fund_id; name/code persist via additional_details
                    additional_details: fundAdditionalDetails,
                }],
                tenant_id: tenantId,
                created_by: userId
            };

            // Ensure API_BASE doesn't have a trailing slash and include tenant ID in the URL
            const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
            const apiUrl = `${baseUrl}/${tenantId}/units-of-work`;

            console.group('API Request Details');
            console.log('Base URL:', baseUrl);
            console.log('Full URL:', apiUrl);
            console.log('Method: POST');
            console.log('Headers:', {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token ? '***' : 'MISSING'}`,
                'Accept': 'application/json'
            });
            console.log('Payload:', payload);
            console.groupEnd();

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            // First check if the response is ok before trying to parse
            if (!response.ok) {
                // For error responses, try to get the error message
                let errorMessage;
                const contentType = response.headers.get('content-type');

                try {
                    if (contentType && contentType.includes('application/json')) {
                        const errorData = await response.json();
                        errorMessage = errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
                    } else {
                        const errorText = await response.text();
                        errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
                    }
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }

                throw new Error(`Failed to create upload session: ${errorMessage}`);
            }

            // Only parse as JSON if response is ok
            let responseData;
            try {
                const responseText = await response.text();
                console.log('Raw response text:', responseText);

                responseData = JSON.parse(responseText);
                console.log('Parsed unit of work response:', {
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url,
                    data: responseData,
                    hasDocuments: !!responseData.documents,
                    documentsCount: responseData.documents?.length || 0
                });

                // Ensure documents array exists and has the expected structure
                if (!responseData.documents || !Array.isArray(responseData.documents)) {
                    console.warn('No documents array in response, adding empty array');
                    responseData.documents = [];
                }

            } catch (parseError) {
                const errorText = await response.text().catch(() => 'Could not read response text');
                console.error('Failed to parse success response as JSON:', parseError, 'Response text:', errorText);
                throw new Error('Server returned invalid JSON response: ' + parseError.message);
            }

            if (!responseData.id) {
                console.error('Invalid response format - missing ID:', responseData);
                throw new Error('Invalid response: Missing unit of work ID');
            }

            setUnitOfWorkId(responseData.id);
            return responseData;

        } catch (error) {
            console.error('Error creating unit of work:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            throw error;
        }
    };

    // Complete the multipart upload
    const completeMultipartUpload = async (fileMeta, parts) => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_BASE}/multipart/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: fileMeta.id,
                    unit_of_work_id: fileMeta.unit_of_work_id,
                    location: fileMeta.location,
                    upload_id: fileMeta.upload_id,
                    name: fileMeta.name,
                    content_type: fileMeta.content_type,
                    parts: parts
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to complete upload:', response.status, errorText);
                throw new Error(`Failed to complete upload: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Upload completed successfully:', result);
            return result;
        } catch (error) {
            console.error('Error completing upload:', error);
            throw error;
        }
    };

    // Get presigned URLs for each part
    const getPresignedUrls = async (file, unitOfWorkId, fileMeta) => {
        // setCurrentStep('Preparing upload...');
        const totalParts = Math.ceil(file.size / (5 * 1024 * 1024));

        try {
            const token = getAuthToken();
            const tenantId = localStorage.getItem('tenant_id');
            const apiUrl = `${API_BASE}/multipart/urls`;

            const payload = {
                id: fileMeta.id, // From unit of work creation response
                tenant_id: tenantId,
                unit_of_work_id: unitOfWorkId,
                location: fileMeta.location, // From unit of work creation response
                upload_id: fileMeta.upload_id, // From unit of work creation response
                name: file.name,
                content_type: file.type || 'application/octet-stream',
                total_parts: totalParts
            };

            console.group('Generating presigned URLs');
            console.log('API URL:', apiUrl);
            console.log('Payload:', payload);
            console.groupEnd();

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to get upload URLs:', response.status, errorText);
                throw new Error(`Failed to get upload URLs: ${response.status} ${response.statusText}`);
            }

            const responseData = await response.json();
            console.log('Presigned URLs response:', responseData);
            return responseData;
        } catch (error) {
            console.error('Error getting presigned URLs:', error);
            throw error;
        }
    };

    // Handle file upload to server using multipart upload
    const handleFileUploadToServer = async (file) => {
        if (uploadInProgressRef.current) {
            return;
        }
        uploadInProgressRef.current = true;
        setIsUploading(true);
        setUploadProgress(0);
        hideToast();

        try {
            const fileNameLower = String(file?.name || '').toLowerCase();
            const isExcelFile = fileNameLower.endsWith('.xlsx') || fileNameLower.endsWith('.xls');
            const isCsvFile = fileNameLower.endsWith('.csv');
            const template = selectedTemplate ? activeTemplateForMapping : null;

            // Multi-sheet Excel: land only the selected worksheet as sheet 1 (e.g. TB).
            let sourceFile = file;
            if (isExcelFile && selectedSheetName) {
                sourceFile = await buildSingleSheetExcelFile(file, selectedSheetName);
            }

            // Parse once up-front so we can both validate headers and build mapped rows.
            const { fields: headerFields, data: sheetRows } = await extractFieldsFromSheet(
                sourceFile,
                selectedSheetName || null
            );

            const selectedDocEarly = documentTypes.find((d) => d.id === selectedDocumentType);
            const documentTypeLabelEarly =
                selectedDocEarly?.name || String(selectedDocumentType || '').trim();
            const isEntityDataUpload = String(documentTypeLabelEarly).trim() === 'Entity Data';
            const isYardiAllvueUpload = isYardiAllvuePocDocumentType(documentTypeLabelEarly);
            // No template: land original Excel/CSV for Snowflake (Tally TB headers are not on row 1).
            // Yardi/Allvue POC types always land the original file for Snowflake ingest.
            const landOriginalSpreadsheet = !template || isYardiAllvueUpload;

            const sourceSystemForSk = systems.find((s) => s.id === selectedSourceGL);
            const tenantSk = String(
                sourceSystemForSk?.surrogateKey ?? sourceSystemForSk?.surrogate_key ?? ''
            ).trim();
            const surrogateKeyForThisUpload =
                String(surrogateKeyLocal ?? '').trim() || tenantSk || '';
            const skTokens = splitSurrogateKeyTokens(surrogateKeyForThisUpload);
            // Entity Data (fund list) is FUND_CODE / FUND_NAME, not GL_CODE. Do not require the source system's TB surrogate key.
            if (skTokens.length > 0 && !isEntityDataUpload && !landOriginalSpreadsheet) {
                const { ok, missing, details } = validateSurrogateKeyHeadersInSheet(
                    headerFields,
                    skTokens,
                    template,
                    excelColumnByTemplateField
                );
                if (!ok) {
                    const need = skTokens.join(', ');
                    const mappedHints = details
                        .filter((d) => d.status === 'mapped' && d.mappedFrom)
                        .map((d) => `${d.token} (mapped from ${d.mappedFrom})`);
                    const mappedSegment =
                        mappedHints.length > 0
                            ? ` Recognized via template mapping: ${mappedHints.join(', ')}.`
                            : '';
                    const msg =
                        `Source GL surrogate key requires these columns in the spreadsheet’s first row: ${need}. ` +
                        `Missing: ${missing.join(', ')}.` +
                        mappedSegment;
                    setErrors([]);
                    showError(msg);
                    return;
                }
            }

            const rowsForBulk = template
                ? applyRefTemplateToRows(sheetRows, template, excelColumnByTemplateField)
                : sheetRows;

            // Match Workflows behavior: for Excel or CSV + selected template, upload mapped JSON to S3.
            // Without a template, upload the selected worksheet only (single-sheet workbook).
            let fileForUpload = sourceFile;
            if (template && (isExcelFile || isCsvFile)) {
                const mappedJson = JSON.stringify(rowsForBulk, null, 2);
                const jsonBlob = new Blob([mappedJson], { type: 'application/json' });
                const jsonFileName = file.name.replace(/\.(xlsx|xls|csv)$/i, '.json');
                fileForUpload = new File([jsonBlob], jsonFileName, { type: 'application/json' });
            }

            // 1. Create unit of work
            const uowData = await createUnitOfWork(fileForUpload);

            // Get file metadata from the unit of work response
            if (!uowData.documents || !Array.isArray(uowData.documents)) {
                console.error('No documents array in unit of work response:', uowData);
                throw new Error('Invalid unit of work response: missing documents array');
            }

            const fileMeta = uowData.documents.find(doc => doc?.name === fileForUpload.name);
            if (!fileMeta) {
                console.error('File metadata not found in unit of work response. Documents:', uowData.documents);
                throw new Error(`File metadata not found in unit of work response. Looking for: ${fileForUpload.name}`);
            }

            // 2. Get presigned URLs
            const urlsData = await getPresignedUrls(fileForUpload, uowData.id, fileMeta);

            // 3. Upload parts
            const totalParts = Math.ceil(fileForUpload.size / (5 * 1024 * 1024));
            const parts = [];

            for (let i = 0; i < totalParts; i++) {
                const start = i * (5 * 1024 * 1024);
                const end = Math.min(start + (5 * 1024 * 1024), fileForUpload.size);
                const partBlob = fileForUpload.slice(start, end);
                const partNumber = i + 1;

                const urlObj = urlsData.urls.find(p => p.part_number === partNumber);
                if (!urlObj) {
                    throw new Error(`No URL found for part ${partNumber}`);
                }

                const etag = await uploadPart(urlObj.url, partBlob, partNumber);
                parts.push({ etag, part_number: partNumber });

                // Update progress
                const progress = Math.round(((i + 1) / totalParts) * 100);
                setUploadProgress(progress);
            }

            // 4. Complete the upload
            const result = await completeMultipartUpload(fileMeta, parts);

            const documentId = result?.id;
            const completedFileName = result?.name ?? fileForUpload.name;
            if (!documentId) {
                throw new Error('Upload completed but no document id was returned.');
            }

            const tenantId = localStorage.getItem('tenant_id');
            const userId = localStorage.getItem('user_id');
            const token = getAuthToken();

            const rawPropertyId =
                localStorage.getItem('property_id') || localStorage.getItem('selected_property_id') || '';
            let propertyIdForApi =
                rawPropertyId && String(rawPropertyId).trim() !== ''
                    ? String(rawPropertyId).trim()
                    : null;
            if (!propertyIdForApi) {
                propertyIdForApi = await fetchDefaultCashReconPropertyId(tenantId, token);
            }

            const selectedDoc = documentTypes.find((d) => d.id === selectedDocumentType);
            const documentTypeLabel = selectedDoc?.name || String(selectedDocumentType);

            const sourceSystem = systems.find((s) => s.id === selectedSourceGL);
            const glSystemName = (sourceSystem?.name || '').toUpperCase();
            const tenantSkForPayload = String(
                sourceSystem?.surrogateKey ?? sourceSystem?.surrogate_key ?? ''
            ).trim();
            const surrogateKeyForPayload =
                String(surrogateKeyLocal ?? '').trim() || tenantSkForPayload || null;

            const notesTrimmed = uploadDescription.trim();

            let transactionsBulk = [];
            let sameDebitCreditRowCount = 0;
            let unequalBothSidesNetRowCount = 0;
            if (!isEntityDataUpload && !landOriginalSpreadsheet) {
                const mapped = mapExtractedRowsToGlTransactionsBulk(
                    rowsForBulk,
                    tenantId,
                    propertyIdForApi,
                    glSystemName,
                    surrogateKeyForPayload
                );
                transactionsBulk = mapped.transactions;
                sameDebitCreditRowCount = mapped.sameDebitCreditRowCount;
                unequalBothSidesNetRowCount = mapped.unequalBothSidesNetRowCount;

                if (transactionsBulk.length === 0) {
                    throw new Error(
                        'No rows could be mapped for bulk import. ' +
                            'Each row needs a transaction date, account description, and a positive debit or credit amount. ' +
                            (selectedTemplate
                                ? 'Check the template comparison: your file headers must match each required Excel column for this template.'
                                : 'Select a GL sync template whose required columns match your file, or use file headers that match the expected GL export names (e.g. GL_DESCRIPTION, TRANSACTION_DATE, DEBIT_AMOUNT).')
                    );
                }
            }

            const targetGlSystem =
                destinationGL != null && String(destinationGL).trim() !== ''
                    ? String(destinationGL).trim().toUpperCase()
                    : null;

            const glHeadersPayload = {
                tenantId,
                propertyId: propertyIdForApi,
                fundId: cardFundId,
                entity: null,
                surrogateKey: surrogateKeyForPayload,
                glSystem: glSystemName,
                targetGlSystem,
                glVersion: '1.0',
                systemType: 'GL_SYNC',
                documentType: documentTypeLabel,
                documentTypeId: selectedDocumentType,
                documentId,
                fileName: completedFileName,
                notes: notesTrimmed || null,
                createdBy: userId,
                updatedBy: userId,
            };

            const glHeadersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-headers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(glHeadersPayload),
            });

            const glHeadersBody = await glHeadersRes.json().catch(() => ({}));
            if (!glHeadersRes.ok) {
                throw new Error(formatConductorErrorMessage(glHeadersBody, glHeadersRes.status));
            }

            const glHeaderId =
                glHeadersBody.glHeaderId ??
                glHeadersBody.id ??
                glHeadersBody.data?.glHeaderId ??
                glHeadersBody.data?.id;
            if (!glHeaderId) {
                throw new Error('GL header saved but the response did not include glHeaderId.');
            }

            let importedCount = 0;
            if (!isEntityDataUpload && !landOriginalSpreadsheet) {
                const transactionsBulkWithHeader = transactionsBulk.map((row) => ({
                    ...row,
                    glHeaderId,
                    propertyId: propertyIdForApi,
                }));

                const bulkUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/bulk`;
                const bulkRes = await fetch(bulkUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(transactionsBulkWithHeader),
                });

                const bulkBody = await bulkRes.json().catch(() => ({}));
                if (!bulkRes.ok) {
                    let msg = formatConductorErrorMessage(bulkBody, bulkRes.status);
                    if (/chk_gl_debit_credit_one_side/i.test(msg)) {
                        msg +=
                            ' Each row must have only a debit or only a credit (not both columns with amounts). If debit and credit are the same number, use one column only, or re-import after fixing the file.';
                    }
                    throw new Error(msg);
                }

                importedCount = Array.isArray(bulkBody) ? bulkBody.length : transactionsBulkWithHeader.length;
            }

            setExtractedData(
                sheetRows.map((row, index) => ({
                    id: index + 1,
                    ...row,
                }))
            );

            const sameDebitCreditMsg =
                sameDebitCreditRowCount > 0
                    ? ` Note: ${sameDebitCreditRowCount} row(s) had the same amount in DEBIT_AMOUNT and CREDIT_AMOUNT; each was imported as debit-only (only one side is allowed per row).`
                    : '';
            const netMergedMsg =
                unequalBothSidesNetRowCount > 0
                    ? ` ${unequalBothSidesNetRowCount} row(s) had different debit vs credit and were imported as one net amount per row.`
                    : '';
            showSuccess(
                isYardiAllvueUpload
                    ? 'File uploaded to S3. Snowflake will ingest it from the GL Sync file-type folder.'
                    : isEntityDataUpload
                    ? 'Entity Data file uploaded. Snowflake will treat this as a fund list from the S3 path.'
                    : landOriginalSpreadsheet
                      ? 'Trial balance file uploaded to S3. Snowflake will read the original Excel (no Conductor row mapping).'
                      : `GL header saved and ${importedCount} transaction(s) imported successfully.${sameDebitCreditMsg}${netMergedMsg}`
            );

            // Log the successful file upload
            try {
                await AuditLoggers.glTransactionFileUploaded(
                    file.name,
                    selectedSourceGL,
                    selectedDocumentType,
                    {
                        fileSize: file.size,
                        fileType: file.type,
                        uploadId: result.uploadId,
                        documentTypeId: selectedDocumentType,
                        sourceGL: selectedSourceGL,
                        glHeaderId,
                        notes: notesTrimmed || undefined,
                        bulkImportedCount: importedCount,
                    }
                );
            } catch (logError) {
                console.error('Error logging GL transaction file upload:', logError);
                // Don't block the operation if logging fails
            }

            setUploadedFile(null);
            setUploadProgress(0);
            setUploadDescription('');
            setSheetFields([]);
            setExcelColumnByTemplateField({});
            setWorkbookSheetNames([]);
            setSelectedSheetName('');
            setFileInputKey((k) => k + 1);

            return result;
        } catch (error) {
            console.error('Error uploading file:', error);
            const detail = error?.message || 'Unknown error';
            const line = `File upload failed: ${detail}`;
            showError(line.length > 450 ? `${line.slice(0, 450)}…` : line);
            // Do not rethrow: callers often omit .catch() on the promise (e.g. Upload button),
            // which would surface as an "Uncaught runtime error" in React dev overlay.
            return undefined;
        } finally {
            uploadInProgressRef.current = false;
            setIsUploading(false);
        }
    };

    // Upload a single part
    const uploadPart = async (url, partData, partNumber) => {
        try {
            const response = await fetch(url, {
                method: 'PUT',
                body: partData
            });

            if (!response.ok) {
                throw new Error(`Failed to upload part ${partNumber}`);
            }

            return response.headers.get('ETag');
        } catch (error) {
            console.error(`Error uploading part ${partNumber}:`, error);
            throw error;
        }
    };

    const handleUploadClick = async () => {
        if (uploadInProgressRef.current || isUploading) {
            return;
        }
        if (!uploadedFile) {
            setErrors(['Please select a file to upload']);
            return;
        }

        if (!selectedDocumentType) {
            setErrors(['Please select a document type']);
            return;
        }

        if (!selectedSourceGL) {
            const selectedDocName = documentTypes.find((d) => d.id === selectedDocumentType)?.name;
            if (!isYardiAllvuePocDocumentType(selectedDocName)) {
            setErrors(['Please select a source GL']);
            return;
            }
        }

        try {
            // Start the multipart upload process
            const result = await handleFileUploadToServer(uploadedFile);

            if (result) {
                try {
                    const { data } = await extractFieldsFromSheet(uploadedFile);
                    if (data.length) {
                        setExtractedData(data.map((row, index) => ({ id: index + 1, ...row })));
                    }
                } catch (err) {
                    console.error('Error parsing file after upload:', err);
                    setErrors(['Error parsing file for preview.']);
                }
            }
        } catch (error) {
            // Errors/toasts are set in handleFileUploadToServer; log here for debugging only.
            console.error('Upload process failed:', error);
        }
    };

    const handleApprove = async () => {
        if (extractedData.length === 0) {
            alert('No data extracted. Please upload a file first.');
            return;
        }

        if (!selectedDocumentType) {
            alert('Please select a document type');
            return;
        }

        if (!selectedSourceGL) {
            alert('Please select a source GL');
            return;
        }

        try {
            const token = getAuthToken();
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('documentTypeId', selectedDocumentType);
            formData.append('systemId', selectedSourceGL);
            formData.append('tenantId', localStorage.getItem('tenant_id'));

            // Here you would typically make an API call to upload the file
            // For example:
            /*
            const response = await fetch(`${API_BASE}/api/upload-mapping`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            
            if (!response.ok) {
                throw new Error('Upload failed');
            }
            
            const result = await response.json();
            */

            // For now, we'll just navigate with the extracted data
            navigate('/gl-sync/gl-mapping-results', {
                state: {
                    data: extractedData,
                    documentType: selectedDocumentType,
                    sourceGL: selectedSourceGL
                }
            });

        } catch (error) {
            console.error('Error uploading file:', error);
            setErrors(['Failed to upload file. Please try again.']);
        }
    };

    /** Tenant-system surrogate key baseline (read-only from API); `surrogateKeyLocal` is editable for this upload only. */
    const tenantSourceSurrogateKeyBaseline = useMemo(() => {
        const row = systems.find((s) => s.id === selectedSourceGL);
        return String(row?.surrogateKey ?? row?.surrogate_key ?? '').trim();
    }, [systems, selectedSourceGL]);

    /** Legacy name kept for any stale HMR / stray JSX; same as tenant baseline (editable value is `surrogateKeyLocal`). */
    const selectedSourceSurrogateKeyDisplay = tenantSourceSurrogateKeyBaseline;

    useEffect(() => {
        setSurrogateKeyLocal(tenantSourceSurrogateKeyBaseline);
        setSurrogateKeyEditUnlocked(false);
    }, [tenantSourceSurrogateKeyBaseline]);

    /** Per template row: logical field, required Excel header (`targetColumn`), match state vs uploaded sheet. */
    const templateColumnMatchRows = useMemo(() => {
        const t = activeTemplateForMapping;
        const m = t?.mappings;
        if (!m) return [];
        return Object.entries(m)
            .filter(([, spec]) => spec && spec.isActive !== false)
            .map(([templateKeyRaw, spec]) => {
                const templateKey = String(templateKeyRaw).trim();
                const expectedHeader = String(spec.targetColumn || templateKey).trim();
                const expectedLower = expectedHeader.toLowerCase();
                const mappedCol = String(excelColumnByTemplateField[templateKey] || '').trim();
                const sheetHasExpected = sheetFields.some(
                    (f) => String(f).trim().toLowerCase() === expectedLower
                );
                const mappedOk =
                    mappedCol !== '' &&
                    sheetFields.some((f) => String(f).trim().toLowerCase() === mappedCol.toLowerCase()) &&
                    mappedCol.toLowerCase() === expectedLower;
                const matched = sheetHasExpected || mappedOk;
                const displayFileCol =
                    mappedCol ||
                    sheetFields.find((f) => String(f).trim().toLowerCase() === expectedLower) ||
                    '';
                return {
                    templateKey,
                    expectedHeader,
                    matched,
                    displayFileCol: displayFileCol || '—',
                };
            });
    }, [activeTemplateForMapping, excelColumnByTemplateField, sheetFields]);

    return (
        <Box
            sx={{
                ...harmonatePageSx,
                ...harmonateFormFieldsSx,
                // Fill main (position:relative) and scroll — do not use flex column here or
                // the card shrinks/clips under overflow:hidden instead of scrolling the page.
                position: 'absolute',
                inset: 0,
                display: 'block',
                height: 'auto',
                minHeight: 0,
                overflow: 'auto',
            }}
        >
            {/* Breadcrumbs — align with GLSyncMapping.jsx */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
                    sx={{
                        mb: 1,
                        '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
                    }}
                >
                    <Link
                        underline="hover"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/gl-sync');
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: ht.white,
                            fontSize: '14px',
                            fontWeight: 500,
                            '&:hover': { color: ht.white },
                        }}
                    >
                        <SyncIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                        GL Sync
                    </Link>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: ht.white,
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    >
                        <UploadFileIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                        Transaction Upload
                    </Box>
                </Breadcrumbs>
                <Typography variant="h6" sx={harmonatePageTitleSx}>
                    Transaction Upload
                </Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    ...harmonateCardSx,
                    p: 3,
                    mb: 3,
                    bgcolor: ht.white,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: 'visible',
                }}
            >
                <Typography variant="subtitle2" sx={{ color: ht.text, fontWeight: 600, mb: 0 }}>
                    Upload settings
                </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 4,
                            alignItems: 'flex-start',
                        }}
                    >
                        {loadingDocumentTypes ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 1,
                                    minHeight: 40,
                                }}
                            >
                                <CircularProgress size={20} />
                                <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                                    Loading document types...
                                </Typography>
                            </Box>
                        ) : documentTypes.length > 0 ? (
                            <TextField
                                label="Document Type"
                                value={selectedDocumentType}
                                onChange={(e) => setSelectedDocumentType(e.target.value)}
                                size="small"
                                fullWidth
                                select
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {documentTypes.map((dt) => (
                                    <MenuItem key={dt.id} value={dt.id}>
                                        {dt.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <Alert severity="warning" sx={{ flex: 1, py: 0.5 }}>
                                GL Sync document types are not enabled for this tenant. Configure them in document type settings.
                            </Alert>
                        )}
                        {loadingSystems ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 1,
                                    minHeight: 40,
                                }}
                            >
                                <CircularProgress size={20} />
                                <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                                    Loading systems...
                                </Typography>
                            </Box>
                        ) : (
                            <TextField
                                label="Source GL"
                                value={selectedSourceGL}
                                onChange={(e) => setSelectedSourceGL(e.target.value)}
                                size="small"
                                fullWidth
                                select
                                disabled={isQuickMapping}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    ...(isQuickMapping
                                        ? {
                                              '& .MuiInputBase-input': { color: ht.text },
                                              bgcolor: ht.listBg,
                                          }
                                        : {}),
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select source GL</em>
                                </MenuItem>
                                {systems.map((system) => (
                                    <MenuItem key={system.id} value={system.id}>
                                        {system.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {!selectedSourceGL || loadingSystems ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 0.5,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                    {loadingSystems ? 'Loading systems…' : 'Select source GL to load templates.'}
                                </Typography>
                            </Box>
                        ) : loadingTemplates ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 1,
                                    minHeight: 40,
                                }}
                            >
                                <CircularProgress size={20} />
                                <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                                    Loading templates…
                                </Typography>
                            </Box>
                        ) : templates.length === 0 ? (
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                    No templates for this GL.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                }}
                            >
                                <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
                                    <InputLabel id="gl-sync-tpl-label">Template</InputLabel>
                                    <Select
                                        labelId="gl-sync-tpl-label"
                                        label="Template"
                                        value={selectedTemplate}
                                        onChange={(e) => handleTemplateChange(e.target.value)}
                                        disabled={isUploading}
                                    >
                                        <MenuItem value="">
                                            <em>No template (file headers must match canonical names)</em>
                                        </MenuItem>
                                        {templates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Tooltip title="Add or edit template fields (same as Reference Templates)">
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Edit reference template mappings"
                                            disabled={isUploading || !selectedTemplate || !activeApiTemplateForEditor}
                                            onClick={() => setTemplateMappingsEditorOpen(true)}
                                            sx={{ mt: 0.5 }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 0.5,
                            }}
                        >
                            <TextField
                                inputRef={surrogateKeyInputRef}
                                label="Surrogate key"
                                value={surrogateKeyLocal}
                                onChange={(e) => setSurrogateKeyLocal(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={isUploading || !selectedSourceGL || !surrogateKeyEditUnlocked}
                                multiline
                                minRows={1}
                                maxRows={2}
                                placeholder={selectedSourceSurrogateKeyDisplay || undefined}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    sx: {
                                        fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                                        fontSize: '12px',
                                    },
                                }}
                                sx={{ flex: 1, minWidth: 0 }}
                            />
                            <Tooltip
                                title={
                                    surrogateKeyEditUnlocked
                                        ? 'Click to lock the field (read-only again).'
                                        : 'Click to edit surrogate key. From tenant GL by default; edit for this upload only (not saved to tenant).'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label={
                                            surrogateKeyEditUnlocked
                                                ? 'Lock surrogate key field'
                                                : 'Unlock surrogate key field to edit'
                                        }
                                        disabled={isUploading || !selectedSourceGL}
                                        onClick={() => {
                                            setSurrogateKeyEditUnlocked((prev) => {
                                                if (prev) {
                                                    surrogateKeyInputRef.current?.blur();
                                                    return false;
                                                }
                                                window.setTimeout(() => {
                                                    surrogateKeyInputRef.current?.focus();
                                                }, 0);
                                                return true;
                                            });
                                        }}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    {selectedSourceGL &&
                        !loadingSystems &&
                        !loadingTemplates &&
                        templates.length === 0 && (
                            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                                No templates returned for this source system. The request tries{' '}
                                <code>documentTypeId</code> + <code>glSystemId</code> via{' '}
                                <code>conductor/ref-templates/by-document-and-gl</code>. Confirm templates exist for this document
                                type and source GL (currently{' '}
                                <strong>
                                    {(systems.find((s) => s.id === selectedSourceGL)?.name || '').trim().toUpperCase() ||
                                        'this GL'}
                                </strong>
                                ).
                            </Alert>
                        )}
                    <TextField
                        label="Description"
                        name="uploadDescription"
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={isUploading}
                        InputLabelProps={{ shrink: true }}
                        // helperText="Optional notes"
                        FormHelperTextProps={{ sx: { mx: 0 } }}
                    />
                    {selectedTemplate && activeTemplateForMapping && templateColumnMatchRows.length > 0 && (
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: ht.cardRadius,
                                border: `1px solid ${ht.line}`,
                                backgroundColor: ht.listBg,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: ht.text }}>
                                Template fields vs your file
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: ht.text }}>
                                Left: template logical field and the Excel header your file must provide (
                                <code>targetColumn</code>). Right: matching column from the uploaded sheet. Green when that header
                                is present; red when it is missing.
                            </Typography>
                            {!sheetFields.length ? (
                                <Typography variant="body2" color="text.secondary">
                                    Upload a file to compare headers.
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 0,
                                        border: `1px solid ${ht.line}`,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        bgcolor: ht.white,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'contents',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 1,
                                                bgcolor: ht.listBg,
                                                borderBottom: `1px solid ${ht.line}`,
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: ht.text,
                                            }}
                                        >
                                            Template → required Excel header
                                        </Box>
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 1,
                                                bgcolor: ht.listBg,
                                                borderBottom: `1px solid ${ht.line}`,
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: ht.text,
                                            }}
                                        >
                                            Column in your file
                                        </Box>
                                    </Box>
                                    {templateColumnMatchRows.map((row, idx) => (
                                        <React.Fragment key={row.templateKey}>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 1.25,
                                                    borderBottom:
                                                        idx < templateColumnMatchRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                    borderRight: { xs: 'none', sm: '1px solid #f3f4f6' },
                                                    fontSize: '13px',
                                                    color: row.matched ? '#166534' : '#b91c1c',
                                                    bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                                                }}
                                            >
                                                <Typography component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
                                                    {row.templateKey}
                                                </Typography>
                                                <Typography component="span" sx={{ color: '#6b7280', mx: 0.75 }}>
                                                    →
                                                </Typography>
                                                <Typography component="span" sx={{ fontFamily: 'monospace', color: 'inherit' }}>
                                                    {row.expectedHeader}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 1.25,
                                                    borderBottom:
                                                        idx < templateColumnMatchRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                                                    fontSize: '13px',
                                                    fontFamily: 'monospace',
                                                    color: row.matched ? '#166534' : '#b91c1c',
                                                    bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                                                }}
                                            >
                                                {row.displayFileCol}
                                            </Box>
                                        </React.Fragment>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                {/* <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
                        Expected column headers (first row of your file)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#495057', fontSize: '13px', lineHeight: 1.6 }}>
                        <strong>Mandatory</strong> for each data row: account code, account description, transaction date, and an{' '}
                        amount via <strong>AMOUNT</strong> (signed) <em>or</em> a <strong>positive</strong> debit and/or credit
                        column. With a <strong>GL sync template</strong>, your file must include the headers shown in the
                        comparison above (each row lists the required header from <code>targetColumn</code>). Without a
                        template, known column aliases are still matched case-insensitively.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '13px', color: '#495057', lineHeight: 1.7 } }}>
                        <li>
                            <strong>GL_CODE</strong> — GL account code <Typography component="span" color="error" fontSize="inherit">*</Typography>
                        </li>
                        <li>
                            <strong>GL_DESCRIPTION</strong> — GL account description{' '}
                            <Typography component="span" color="error" fontSize="inherit">*</Typography>
                        </li>
                        <li>
                            <strong>TRANSACTION_DATE</strong> — Posting / transaction date{' '}
                            <Typography component="span" color="error" fontSize="inherit">*</Typography>
                        </li>
                        <li>
                            <strong>AMOUNT</strong> — Single amount column <Typography component="span" color="error" fontSize="inherit">*</Typography>{' '}
                            (numeric): <strong>positive</strong> values import as debit, <strong>negative</strong> as credit. Use
                            this <em>or</em> separate debit/credit columns below—not both patterns on the same row unless the
                            template maps only one side.
                        </li>
                        <li>
                            <strong>DEBIT_AMOUNT</strong>, <strong>CREDIT_AMOUNT</strong> —{' '}
                            <Typography component="span" color="error" fontSize="inherit">*</Typography> Alternative to{' '}
                            <strong>AMOUNT</strong>: at least one positive number per row. Prefer one column per row. If both hold
                            the <strong>same positive value</strong>, the import stores one debit to satisfy the one-side rule.
                        </li>
                        <li>
                            <strong>TRANSACTION_TYPE</strong>, <strong>FUND_ID</strong>, <strong>ENTITY</strong>,{' '}
                            <strong>BATCH_ID</strong>, <strong>JOURNAL_CODE</strong>,{' '}
                            <strong>CURRENCY</strong> — optional when present in the file.
                        </li>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#c62828', fontSize: '12px' }}>
                        * Required for import (amount: map <strong>AMOUNT</strong> signed, or map at least one of{' '}
                        <strong>DEBIT_AMOUNT</strong> / <strong>CREDIT_AMOUNT</strong> positive per row).
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6c757d', fontSize: '12px' }}>
                        File format: CSV or Excel (.xlsx / .xls), first sheet is read. With a template, mapped values are written
                        to the canonical column names used for import; without one, headers are matched case-insensitively to
                        known field names.
                    </Typography>
                </Box> */}

                <Box>
                <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: ht.text }}>
                    Upload file
                </Typography>

                <Box sx={{ position: 'relative' }}>
                    <HiddenInput
                        key={fileInputKey}
                        accept=".csv,.xls,.xlsx"
                        id="file-upload"
                        type="file"
                        disabled={isUploading}
                        onChange={handleFileUpload}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
                        {isUploading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                                borderRadius: 1,
                                p: 2,
                                textAlign: 'center'
                            }}>
                                <CircularProgress sx={{ mb: 2 }} />
                                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                    {currentStep}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {Math.round(uploadProgress)}% complete
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {/* <label htmlFor="file-upload">
                        <UploadArea sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ textAlign: 'left', flexGrow: 1 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: ht.secondaryTab,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                }}>
                                    <CloudUploadIcon sx={{ fontSize: 20, color: ht.green }} />
                                </Box>
                                <Typography variant="body1" sx={{ mb: 1, color: ht.text, fontSize: '14px' }}>
                                    Drag your file(s) or{' '}
                                    <Typography component="span" sx={{
                                        color: ht.green,
                                        textDecoration: 'none',
                                        fontWeight: 500
                                    }}>
                                        browse
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" sx={{ color: ht.fadedText, fontSize: '12px' }}>
                                    Max 10 MB files are allowed
                                </Typography>
                            </Box>
                        </UploadArea>
                    </label> */}
                    <label
                        htmlFor="file-upload"
                        style={{
                            pointerEvents: isUploading ? 'none' : 'auto',
                            cursor: isUploading ? 'default' : 'pointer',
                        }}
                    >
                        <UploadArea
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            sx={{
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                border: isDragging ? `2px dashed ${ht.green}` : undefined,
                                backgroundColor: isDragging ? ht.secondaryTab : undefined,
                                transition: 'all 0.3s ease',
                                opacity: isUploading ? 0.65 : 1,
                            }}
                        >
                            <Box sx={{ textAlign: 'left', flexGrow: 1 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(46, 166, 160, 0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                }}>
                                    <CloudUploadIcon sx={{ fontSize: 20, color: ht.text }} />
                                </Box>
                                <Typography variant="body1" sx={{ mb: 1, color: ht.text, fontSize: '14px' }}>
                                    {isDragging ? 'Drop your file here' : (
                                        <>
                                            Drag your file(s) or{' '}
                                            <Typography component="span" sx={{
                                                color: ht.green,
                                                textDecoration: 'none',
                                                fontWeight: 500
                                            }}>
                                                browse
                                            </Typography>
                                        </>
                                    )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: ht.fadedText, fontSize: '12px' }}>
                                    Max 10 MB files are allowed
                                </Typography>
                            </Box>
                        </UploadArea>
                    </label>
                    <Typography variant="caption" sx={{
                        color: ht.fadedText,
                        display: 'block',
                        fontSize: '11px',
                        mb: 2
                    }}>
                        Only support CSV and XLSX
                    </Typography>

                    {uploadedFile && workbookSheetNames.length > 0 && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel id="gl-sync-worksheet-label">Worksheet</InputLabel>
                            <Select
                                labelId="gl-sync-worksheet-label"
                                label="Worksheet"
                                value={selectedSheetName || ''}
                                onChange={(e) => handleWorksheetChange(e.target.value)}
                                disabled={isUploading}
                            >
                                {workbookSheetNames.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                        {String(name).trim().toUpperCase() === 'TB' ? ' (Trial Balance)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" sx={{ mt: 0.75, color: ht.fadedText, display: 'block' }}>
                                Only the selected worksheet is uploaded (as sheet 1). For Tally trial balance, choose{' '}
                                <strong>TB</strong>. Headers do not need to be on row 1 when no template is selected.
                            </Typography>
                        </FormControl>
                    )}

                    {uploadedFile && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 2,
                                backgroundColor: ht.listBg,
                                border: `1px solid ${ht.line}`,
                                borderRadius: ht.fieldRadius,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Box sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    backgroundColor: '#e3f2fd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <InsertDriveFileIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#000000',
                                        fontSize: '14px',
                                        mb: 0.5
                                    }}>
                                        {uploadedFile.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                                        {(uploadedFile.size / 1024).toFixed(2)} KB
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                size="small"
                                disabled={isUploading}
                                onClick={() => {
                                    setUploadedFile(null);
                                    setExtractedData([]);
                                    setSheetFields([]);
                                    setExcelColumnByTemplateField({});
                                    setWorkbookSheetNames([]);
                                    setSelectedSheetName('');
                                    setUploadProgress(0);
                                    setUploadDescription('');
                                    setFileInputKey((k) => k + 1);
                                    hideToast();
                                }}
                                sx={{
                                    color: '#000000',
                                    '&:hover': { color: ht.red, backgroundColor: 'rgba(239, 63, 63, 0.1)' }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch' }}>
                    {uploadedFile && (
                        <Button
                            type="button"
                            variant="contained"
                            size="small"
                            onClick={() => handleFileUploadToServer(uploadedFile)}
                            disabled={
                                isUploading ||
                                !selectedDocumentType ||
                                (
                                    !selectedSourceGL &&
                                    !isYardiAllvuePocDocumentType(
                                        documentTypes.find((d) => d.id === selectedDocumentType)?.name
                                    )
                                ) ||
                                (workbookSheetNames.length > 0 && !selectedSheetName) ||
                                (!isHarmonateUser && !hasPermission('GL Sync', 'upload'))
                            }
                            sx={harmonateProceedButtonSx}
                        >
                            {isUploading ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Uploading…
                                </>
                            ) : (
                                'Upload File'
                            )}
                        </Button>
                    )}
                </Box>
                </Box>
            </Paper>

            {/* Error Display */}
            {errors.length > 0 && (
                <Box sx={{ p: 2, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }}>
                    {errors.map((error, index) => (
                        <Box
                            key={index}
                            sx={{
                                backgroundColor: 'rgba(239, 63, 63, 0.12)',
                                color: ht.red,
                                p: 2,
                                mb: 1,
                                borderRadius: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography>{error}</Typography>
                            <IconButton size="small" onClick={() => setErrors(errors.filter((_, i) => i !== index))}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}

            <RefTemplateMappingsEditorDrawer
                open={templateMappingsEditorOpen}
                onClose={() => setTemplateMappingsEditorOpen(false)}
                apiTemplate={activeApiTemplateForEditor}
                getAuthToken={getAuthToken}
                persistToApi={false}
                onLocalMappingChange={(templateId, mapping) => {
                    setLocalRefTemplateMappingById((prev) => ({ ...prev, [templateId]: mapping }));
                }}
            />

            <Toast
                open={toastState.open}
                onClose={hideToast}
                message={toastState.message}
                severity={toastState.severity}
                title={toastState.title}
                autoHideDuration={toastState.autoHideDuration}
                position={toastState.position}
            />
        </Box>
    );
};

export default GLSyncGLMappingWorkflow;